<template>
  <div
    ref="$el"
    class="d-grid"
    :style="style"
    @click="e => $emit('click', e)"
    @mousedown="e => handleMousedown(e)"
    @mouseenter="e => handleMouseenter(e)"
    @mouseleave="e => handleMouseleave(e)"
    @mouseup="e => handleMouseup(e)"
  >
    <slot />
  </div>
</template>
<script setup lang="ts">
  import {
    ref, reactive, watch, watchEffect, computed, withDefaults,
  } from 'vue'
  import { MouseState } from './interface'

  const props = withDefaults(
    defineProps<{
      /**
       * 设置 grid 为行内元素
       */
      inline?: boolean
      /**
       * 设置 `grid-template-rows`:
       * - 如果为 `string` 则直接设置为对应样式 https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-rows
       * - 如果为 `number` 则设置为 `repeat(${rows}, minmax(0, 1fr))`
       */
      rows?: string | number
      /**
       * 设置 `grid-template-columns`:
       * - 如果为 `string` 则直接设置为对应样式 https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns
       * - 如果为 `number` 则设置为 `repeat(${columns}, minmax(0, 1fr))`
       */
      columns?: string | number
      /**
       * 设置 `行间距`
       */
      rowGap?: string | number
      /**
       * 设置 `列间距`
       */
      columnGap?: string | number
    }>(),
    {
      inline: false,
      rows: undefined,
      columns: undefined,
      rowGap: undefined,
      columnGap: undefined,
    },
  )

  const emits = defineEmits<{(e: 'click', event: MouseEvent): void; (e: 'mousedown', event: MouseEvent): void; (e: 'mouseenter', event: MouseEvent): void; (e: 'mouseleave', event: MouseEvent): void; (e: 'mouseup', event: MouseEvent): void; (e: 'state-change', state: MouseState): void}>()

  const state = reactive<MouseState>({
    pressing: false,
    hover: false,
  })

  watch(
    state,
    v => emits('state-change', v),
  )

  const style = computed(
    () => ({
      display: props.inline
        ? 'inline-grid'
        : 'grid',
      ...props.inline && {
        verticalAlign: 'top',
      },
      ...props.rows && {
        gridTemplateRows: typeof props.rows === 'number'
          ? `repeat(${props.rows}, minmax(0, 1fr))`
          : props.rows,
      },
      ...props.columns && {
        gridTemplateColumns: typeof props.columns === 'number'
          ? `repeat(${props.columns}, minmax(0, 1fr))`
          : props.columns,
      },
      ...props.rowGap && {
        rowGap: typeof props.rowGap === 'number'
          ? `${props.rowGap}px`
          : props.rowGap,
      },
      ...props.columnGap && {
        columnGap: typeof props.columnGap === 'number'
          ? `${props.columnGap}px`
          : props.columnGap,
      },
    }),
  )

  function handleMousedown(e: MouseEvent) {
    emits('mousedown', e)
    state.pressing = true
  }

  function handleMouseenter(e: MouseEvent) {
    emits('mouseenter', e)
    state.hover = true
  }

  function handleMouseleave(e: MouseEvent) {
    emits('mouseleave', e)
    state.pressing = false
    state.hover = false
  }

  function handleMouseup(e: MouseEvent) {
    emits('mouseup', e)
    state.pressing = false
  }

  const $el = ref<HTMLElement>()

  const exposed = reactive({
    $el: $el.value,
  })

  watchEffect(
    () => {
      exposed.$el = $el.value
    },
  )

  defineExpose(exposed)
</script>
